import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPopover } from './useAnchoredPopover';
import {
  INVESTMENT_BLURB,
  INVESTMENT_CLASSES,
  INVESTMENT_LABEL,
  INVESTMENT_TONE,
} from '../investment';
import type { InvestmentClass } from '../types';

interface Props {
  /** The EFFECTIVE class shown on the trigger — the override when set, else derived. */
  value: InvestmentClass;
  /** The type-derived class — named by "Reset to auto" so the choice is never blind. */
  derived: InvestmentClass;
  /** Whether an override is currently in force (controls the Reset-to-auto row). */
  overridden: boolean;
  disabled?: boolean;
  /** A muted qualifier shown inline after the value — e.g. "· from type" — so the
   *  provenance reads on one line, the fuller note living in the field's (i). */
  provenance?: ReactNode;
  /** The card's scroll area, so the menu flips to fit the card low in the modal. */
  boundaryRef?: RefObject<HTMLElement | null>;
  /** Pick a class, or 'auto' to clear the override and follow the type again. The
   *  caller stores only the difference (picking the derived value clears it too). */
  onChange: (next: InvestmentClass | 'auto') => void;
}

/**
 * RUN vs CHANGE — the card's investment-class field (#435, surface 1b).
 *
 * The same app field + portaled menu as the card's other pick-one controls
 * (PickField/AreaField): the shared `.ctl.quiet.area-trigger` opens an `.sr-ta`
 * popover pinned by useAnchoredPopover, closing on click-away and Escape, never a
 * native menu. It differs in two ways the mock asks for: each option carries a
 * one-line gloss, and a "Reset to auto" footer appears ONLY once overridden, naming
 * what auto currently is. The close/focus handling is ported verbatim from PickField
 * so the WebKit focus and Escape-ordering fixes it carries are not regressed.
 */
export default function InvestmentField({ value, derived, overridden, disabled = false, provenance, boundaryRef, onChange }: Props) {
  const [open, setOpen] = useState(false);
  // Rows the arrows/Enter walk: the two classes, then (when overridden) the reset row.
  const rows: (InvestmentClass | 'auto')[] = overridden ? [...INVESTMENT_CLASSES, 'auto'] : [...INVESTMENT_CLASSES];
  const [active, setActive] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  const pos = useAnchoredPopover(open, trigger, pop, boundaryRef);
  const visible = open && !!pos;

  // Open on the current class.
  useEffect(() => {
    if (!open) return;
    const i = rows.indexOf(value);
    setActive(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Move the keyboard into the list once it is actually visible (focusing while
  // still hidden is dropped by the browser — PickField, 17 Oct).
  useEffect(() => {
    if (!visible) return;
    pop.current?.focus({ preventScroll: true });
  }, [visible]);

  useEffect(() => {
    if (!open) return;
    pop.current?.querySelector('.inv-opt.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Click-away / Escape (document CAPTURE) / Tab-away (focusout) — ported from
  // PickField so the portaled menu wins Escape over the card's own window listener
  // and closes when focus leaves, without mistaking a settling click for a Tab-away.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (trigger.current?.contains(t) || pop.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        trigger.current?.focus({ preventScroll: true });
      }
    };
    const onFocusOut = () => {
      setTimeout(() => {
        const el = document.activeElement;
        if (!el || el === document.body) return;
        if (trigger.current?.contains(el) || pop.current?.contains(el)) return;
        setOpen(false);
      }, 0);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  const choose = (v: InvestmentClass | 'auto') => {
    onChange(v);
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  const move = (d: number) => setActive((a) => Math.min(Math.max(0, a + d), rows.length - 1));

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus({ preventScroll: true });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(rows.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (rows[active]) choose(rows[active]);
    }
  };

  return (
    <span className="area-combo inv-combo">
      <button
        ref={trigger}
        type="button"
        className="ctl quiet area-trigger inv-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Investment class"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={`inv-swatch ${INVESTMENT_TONE[value]}`} aria-hidden="true" />
        <span className={`inv-trigger-name ${INVESTMENT_TONE[value]}`}>{INVESTMENT_LABEL[value]}</span>
        {provenance != null && provenance !== '' && <span className="inv-prov">{provenance}</span>}
      </button>

      {open && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop inv-pop"
          role="listbox"
          aria-label="Investment class"
          tabIndex={-1}
          onKeyDown={onListKeyDown}
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            minWidth: pos?.anchorW,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div className="sr-ta-body">
            <span className="sr-ta-label">Investment class</span>
            {INVESTMENT_CLASSES.map((c) => {
              const i = rows.indexOf(c);
              const on = c === value;
              return (
                <button
                  key={c}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`sr-ta-opt inv-opt ${i === active ? 'is-active' : ''} ${on ? 'is-on' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(c)}
                >
                  <span className="inv-opt-check" aria-hidden="true">
                    {on && (
                      <svg width="14" height="14" viewBox="0 0 14 14">
                        <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span className="inv-opt-text">
                    <span className="inv-opt-head">
                      <span className={`inv-swatch ${INVESTMENT_TONE[c]}`} aria-hidden="true" />
                      <span className={`inv-opt-name ${INVESTMENT_TONE[c]}`}>{INVESTMENT_LABEL[c]}</span>
                    </span>
                    <span className="inv-opt-blurb">{INVESTMENT_BLURB[c]}</span>
                  </span>
                </button>
              );
            })}
            {overridden && (
              <>
                <div className="inv-sep" role="separator" />
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className={`sr-ta-opt inv-opt inv-reset ${active === rows.indexOf('auto') ? 'is-active' : ''}`}
                  onMouseEnter={() => setActive(rows.indexOf('auto'))}
                  onClick={() => choose('auto')}
                >
                  <span className="inv-opt-check" aria-hidden="true" />
                  <span className="inv-opt-text">
                    <span className="inv-opt-name">Reset to auto</span>
                    <span className="inv-opt-blurb">Follow the type again (currently {INVESTMENT_LABEL[derived]}).</span>
                  </span>
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
